import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import PaymentSummary from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/pay/paymentSummary";
import ContactDetails from "../../../components/newPersonalCyberPolicy/personalCyberFlowSteps/planSelection/contactDetails";
import SumInsuredSelection from "../../../components/newPersonalCyberPolicy/personalCyberFlowSteps/planSelection/sumInsuredSelection";
import StartPage from "../../../components/newPersonalCyberPolicy/personalCyberFlowSteps/start/startPage";
import {
  STEP_NAME_PAY,
  STEP_NAME_PLAN_SELECTION,
  STEP_NAME_START,
} from "../../../hooks/data";
import usePersonalCyberFlow from "../../../hooks/usePersonalCyberFlow";
import { clearPersonalCyberFlow } from "../../../redux/features/personalCyberFlowSlice";
import { clearNewPersonalCyberFormData } from "../../../redux/features/personalCyberFormSlice";
import { personalCyberStepperData } from "./data";

const NewPersonalCyberPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep } = usePersonalCyberFlow();

  // Clear payment redirect flag on component mount (user returned from payment or started fresh)
  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem("isRedirectingToPayment");
    if (wasRedirectingToPayment === "true") {
      console.log("🔓 Clearing payment redirect flag - user returned or component mounted");
      sessionStorage.removeItem("isRedirectingToPayment");
    }
  }, []);

  // Clear form data when component unmounts or when user navigates away
  useEffect(() => {
    const clearAllData = () => {
      dispatch(clearPersonalCyberFlow());
      dispatch(clearNewPersonalCyberFormData());
      // Clear sessionStorage items
      sessionStorage.removeItem("wasGuestPlanSelection");
      sessionStorage.removeItem("wasGuest");
      sessionStorage.removeItem("loginSuccess");
      // Clear localStorage items
      localStorage.removeItem("personal_cyber_basic_info");
      localStorage.removeItem("guest_basic_info");
    };

    const handleBeforeUnload = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      clearAllData();
    };

    const handlePopState = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      clearAllData();
    };

    // Add event listeners for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function - runs when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // CRITICAL: Don't clear flow state if we're redirecting to payment
      // This prevents the brief flash of the start screen before redirect
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on unmount - redirecting to payment");
        return;
      }

      // Clear form data when component unmounts
      clearAllData();
    };
  }, [dispatch]);

  // Clear form data when user navigates away from the personal cyber policy route
  useEffect(() => {
    const currentPath = location.pathname;

    const clearAllData = () => {
      dispatch(clearPersonalCyberFlow());
      dispatch(clearNewPersonalCyberFormData());
      // Clear sessionStorage items
      sessionStorage.removeItem("wasGuestPlanSelection");
      sessionStorage.removeItem("wasGuest");
      sessionStorage.removeItem("loginSuccess");
      // Clear localStorage items
      localStorage.removeItem("personal_cyber_basic_info");
      localStorage.removeItem("guest_basic_info");
    };

    return () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on route change - redirecting to payment");
        return;
      }

      // Only clear if we're leaving the personal cyber policy routes
      if (!currentPath.includes("/personal-cyber")) {
        clearAllData();
      }
    };
  }, [location.pathname, dispatch]);

  const activeStepData = useMemo(() => {
    let activeStep = personalCyberStepperData.find(
      (each) => each.key === currentStep.name
    );
    return activeStep;
  }, [currentStep]);

  const PageRenderer = () => {
    // Always show StartPage if currentStep is undefined or at the start
    if (!currentStep?.name || currentStep?.name === STEP_NAME_START) {
      return <StartPage onClose={handleModalClose} />;
    }

    switch (currentStep?.name) {
      case STEP_NAME_PLAN_SELECTION:
        // Handle substeps for plan selection
        if (currentSubstep === "contact_details") {
          return <ContactDetails />;
        }
        return <SumInsuredSelection />; // Default to choose_plan substep
      case STEP_NAME_PAY:
        // Default to review_and_confirm substep (payment summary)
        return <PaymentSummary />;
      default:
        return <StartPage onClose={handleModalClose} />; // Fallback to start page
    }
  };

  const handleModalClose = () => {
    // Reset personal cyber flow and form data when modal is closed
    dispatch(clearPersonalCyberFlow());
    dispatch(clearNewPersonalCyberFormData());

    // Clear all sessionStorage items related to personal cyber flow
    sessionStorage.removeItem("wasGuestPlanSelection");
    sessionStorage.removeItem("wasGuest");
    sessionStorage.removeItem("isRedirectingToPayment");
    sessionStorage.removeItem("loginSuccess");

    // Clear localStorage items
    localStorage.removeItem("personal_cyber_basic_info");
    localStorage.removeItem("guest_basic_info"); // Fallback key

    console.log("✅ Personal Cyber flow reset - all data cleared (Redux + sessionStorage + localStorage)");

    setShowModal(false);
    navigate("/");
  };

  return (
    <Modal show={showModal}>
      <Header
        isPopup
        hideMobileClose
        onCloseModal={handleModalClose}
        showStepper={true}
        steps={personalCyberStepperData}
        activeStep={activeStepData}
      />
      <div id="pc-back-portal" />
      <div className="mainPersonalCyberModal pt-5 relative pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-5 ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={currentStep?.name || "start"}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {PageRenderer()}
          </motion.div>
        </AnimatePresence>
        {/* Stable mount point for the bottom sticky footer button, kept
            outside the animated/transformed wrapper to prevent flicker. */}
        <div id="pc-footer-portal" />
      </div>
    </Modal>
  );
};

export default NewPersonalCyberPolicy;
